import { XYPosition } from '@xyflow/react';
import { subscribeWithSelector } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import type { GanttPdfRenderMode } from 'components/Gantt/GanttPdfExportV2/types/pdfExportTypes';
import { CHAT_SERVER_URL_KEY, SHOW_MPP_DUMMY_RESPONSE_KEY, SHOW_PASTE_ACTIVITY_IDS_DIALOG_KEY } from 'state_management/constants';
import { IN_PRODUCTION, REMOTE_CHAT_SERVER_URL } from 'state_management/env_constants';

import { SetterOrUpdater } from './uiSlice';

export type FullPageLoaderPriority = 'appBootstrap' | 'projectBootstrap' | 'viewTransition' | 'viewInitialization' | 'operation';
export type FullPageLoaderScope = 'app' | 'content';

export type FullPageLoaderRequest = {
  id: string;
  message: string;
  priority: FullPageLoaderPriority;
  scope: FullPageLoaderScope;
  autoId?: string;
  enterDelay?: number;
  exitDelay?: number;
  order: number;
};

export type FullPageLoaderInput = Omit<FullPageLoaderRequest, 'order' | 'scope'> & {
  scope?: FullPageLoaderScope;
};

const FULL_PAGE_LOADER_PRIORITY_ORDER: Record<FullPageLoaderPriority, number> = {
  appBootstrap: 5,
  projectBootstrap: 4,
  viewTransition: 3,
  viewInitialization: 2,
  operation: 1
};

export const getActiveFullPageLoader = (fullPageLoaders: Record<string, FullPageLoaderRequest>, scope?: FullPageLoaderScope): FullPageLoaderRequest | null => {
  const requests = Object.values(fullPageLoaders).filter((request) => (scope ? request.scope === scope : true));

  if (!requests.length) {
    return null;
  }

  return requests.reduce<FullPageLoaderRequest | null>((current, candidate) => {
    if (!current) {
      return candidate;
    }

    const currentPriority = FULL_PAGE_LOADER_PRIORITY_ORDER[current.priority];
    const candidatePriority = FULL_PAGE_LOADER_PRIORITY_ORDER[candidate.priority];

    if (candidatePriority > currentPriority) {
      return candidate;
    }

    if (candidatePriority < currentPriority) {
      return current;
    }

    return candidate.order < current.order ? candidate : current;
  }, null);
};

export const selectActiveFullPageLoader =
  (scope: FullPageLoaderScope) =>
  (state: AppStore): FullPageLoaderRequest | null =>
    getActiveFullPageLoader(state.fullPageLoaders, scope);

export const getVisibleFullPageLoader = (fullPageLoaders: Record<string, FullPageLoaderRequest>, scope: FullPageLoaderScope): FullPageLoaderRequest | null => {
  if (scope === 'content' && getActiveFullPageLoader(fullPageLoaders, 'app')) {
    return null;
  }

  return getActiveFullPageLoader(fullPageLoaders, scope);
};

export const selectVisibleFullPageLoader =
  (scope: FullPageLoaderScope) =>
  (state: AppStore): FullPageLoaderRequest | null =>
    getVisibleFullPageLoader(state.fullPageLoaders, scope);

interface AppStore {
  organizationAuthIssue: boolean;
  projectAuthFailureReason: string | null;
  sideNavExpandedState: boolean;
  setSideNavExpandedState: SetterOrUpdater<boolean>;
  // Mouse State
  mousePosition: XYPosition;
  getMousePosition: () => XYPosition;
  setMousePosition: SetterOrUpdater<XYPosition>;
  showScrollToButtonAtBottom: boolean;
  setShowScrollToButtonAtBottom: SetterOrUpdater<boolean>;
  amplitudeInitialized: boolean;
  setAmplitudeInitialized: SetterOrUpdater<boolean>;
  chatServerUrl: string;
  setChatServerUrl: SetterOrUpdater<string>;
  showMPPDummyResponse: boolean;
  setShowMPPDummyResponse: SetterOrUpdater<boolean>;
  pasteActivityIdsDialogEnabled: boolean;
  setPasteActivityIdsDialogEnabled: SetterOrUpdater<boolean>;
  fullPageLoaders: Record<string, FullPageLoaderRequest>;
  fullPageLoaderSequence: number;
  showFullPageLoader: (loader: FullPageLoaderInput) => void;
  hideFullPageLoader: (loaderId: string) => void;
  updateFullPageLoader: (loaderId: string, patch: Partial<Omit<FullPageLoaderRequest, 'id' | 'order'>>) => void;
  pdfRenderMode: GanttPdfRenderMode | null;
  setPdfRenderMode: (value: GanttPdfRenderMode | null) => void;
  groupReorderEntryDialogOpen: boolean;
  openGroupReorderEntryDialog: () => void;
  clearGroupReorderEntryDialog: () => void;
}

export const useAppStore = createWithEqualityFn<AppStore>()(
  subscribeWithSelector((set, get) => ({
    organizationAuthIssue: false,
    projectAuthFailureReason: null,
    sideNavExpandedState: false,
    setSideNavExpandedState: (valOrUpdater) => {
      if (typeof valOrUpdater === 'boolean') {
        set({ sideNavExpandedState: valOrUpdater });
      } else {
        set({ sideNavExpandedState: valOrUpdater(get().sideNavExpandedState) });
      }
    },
    // Mouse State
    mousePosition: { x: 0, y: 0 },
    getMousePosition: () => {
      return get().mousePosition;
    },
    setMousePosition: (valOrUpdater) => {
      if (typeof valOrUpdater === 'object') {
        set({ mousePosition: valOrUpdater });
      } else {
        set({ mousePosition: valOrUpdater(get().mousePosition) });
      }
    },
    showScrollToButtonAtBottom: false,
    setShowScrollToButtonAtBottom: (valOrUpdater) => {
      if (typeof valOrUpdater === 'boolean') {
        set({ showScrollToButtonAtBottom: valOrUpdater });
      } else {
        set({ showScrollToButtonAtBottom: valOrUpdater(get().showScrollToButtonAtBottom) });
      }
    },
    amplitudeInitialized: false,
    setAmplitudeInitialized: (valOrUpdater) => {
      const prev = get().amplitudeInitialized;
      const next = typeof valOrUpdater === 'boolean' ? valOrUpdater : valOrUpdater(prev);
      if (prev === next) return;
      set({ amplitudeInitialized: next });
    },
    chatServerUrl: (() => {
      if (!IN_PRODUCTION) {
        const saved = localStorage.getItem(CHAT_SERVER_URL_KEY);
        if (saved) return saved;
      }
      return REMOTE_CHAT_SERVER_URL;
    })(),
    setChatServerUrl: (valOrUpdater) => {
      const prev = get().chatServerUrl;
      const next = typeof valOrUpdater === 'string' ? valOrUpdater : valOrUpdater(prev);
      if (prev === next) return;
      set({ chatServerUrl: next });
      if (!IN_PRODUCTION) {
        try {
          localStorage.setItem(CHAT_SERVER_URL_KEY, next);
        } catch (error) {
          logger.debug('Failed to save chatServerUrl to localStorage', { extra: { error } });
        }
      }
    },
    showMPPDummyResponse: (() => {
      if (IN_PRODUCTION) return false;
      try {
        return localStorage.getItem(SHOW_MPP_DUMMY_RESPONSE_KEY) === 'true';
      } catch (error) {
        logger.debug('Failed to read showMPPDummyResponse from localStorage', { extra: { error } });
        return false;
      }
    })(),
    setShowMPPDummyResponse: (valOrUpdater) => {
      const prev = get().showMPPDummyResponse;
      const next = typeof valOrUpdater === 'boolean' ? valOrUpdater : valOrUpdater(prev);
      if (prev === next) return;
      set({ showMPPDummyResponse: next });
      if (!IN_PRODUCTION) {
        try {
          localStorage.setItem(SHOW_MPP_DUMMY_RESPONSE_KEY, String(next));
        } catch (error) {
          logger.debug('Failed to save showMPPDummyResponse to localStorage', { extra: { error } });
        }
      }
    },
    pasteActivityIdsDialogEnabled: (() => {
      if (IN_PRODUCTION) return false;
      try {
        return localStorage.getItem(SHOW_PASTE_ACTIVITY_IDS_DIALOG_KEY) === 'true';
      } catch (error) {
        logger.debug('Failed to read pasteActivityIdsDialogEnabled from localStorage', { extra: { error } });
        return false;
      }
    })(),
    setPasteActivityIdsDialogEnabled: (valOrUpdater) => {
      const prev = get().pasteActivityIdsDialogEnabled;
      const next = typeof valOrUpdater === 'boolean' ? valOrUpdater : valOrUpdater(prev);
      if (prev === next) return;
      set({ pasteActivityIdsDialogEnabled: next });
      if (!IN_PRODUCTION) {
        try {
          localStorage.setItem(SHOW_PASTE_ACTIVITY_IDS_DIALOG_KEY, String(next));
        } catch (error) {
          logger.debug('Failed to save pasteActivityIdsDialogEnabled to localStorage', { extra: { error } });
        }
      }
    },
    fullPageLoaders: {},
    fullPageLoaderSequence: 0,
    showFullPageLoader: (loader) => {
      const existing = get().fullPageLoaders[loader.id];

      if (existing) {
        set((state) => ({
          fullPageLoaders: {
            ...state.fullPageLoaders,
            [loader.id]: {
              ...existing,
              ...loader,
              scope: loader.scope ?? existing.scope
            }
          }
        }));
        return;
      }

      const nextOrder = get().fullPageLoaderSequence + 1;

      set((state) => ({
        fullPageLoaderSequence: nextOrder,
        fullPageLoaders: {
          ...state.fullPageLoaders,
          [loader.id]: {
            ...loader,
            scope: loader.scope ?? 'content',
            order: nextOrder
          }
        }
      }));
    },
    hideFullPageLoader: (loaderId) => {
      const loaders = get().fullPageLoaders;
      if (!loaders[loaderId]) return;

      const { [loaderId]: _, ...rest } = loaders;
      set({ fullPageLoaders: rest });
    },
    updateFullPageLoader: (loaderId, patch) => {
      set((state) => {
        const existing = state.fullPageLoaders[loaderId];

        if (!existing) {
          return state;
        }

        return {
          fullPageLoaders: {
            ...state.fullPageLoaders,
            [loaderId]: {
              ...existing,
              ...patch
            }
          }
        };
      });
    },
    pdfRenderMode: null,
    setPdfRenderMode: (value) => set({ pdfRenderMode: value }),
    groupReorderEntryDialogOpen: false,
    openGroupReorderEntryDialog: () => set({ groupReorderEntryDialogOpen: true }),
    clearGroupReorderEntryDialog: () => set({ groupReorderEntryDialogOpen: false })
  })),
  shallow
);
